import { useContext } from "react";

import {
  DataContext,
  DataContextInterface,
} from "@/provider/DataContextProvider";
import { ChargeTypeIDs, mobileAbos } from "@/utils/constants";
import {
  getEntry,
  hasBasketEntryWithVoiceOption,
} from "@/utils/dataContextHelpers";
import { getIconForDevice } from "@/utils/deviceUtils";
import { isFlatHomeTariff } from "@/utils/tariffUtils";

import { useGetCampaignEndDate } from "./useGetCampaignEndDate";

export const useGetFlatPricesAndName: (isRecurring?: boolean) => {
  onceTotal: number;
  onceSubtotal: number;
  recurringTotal: number;
  recurringSubtotal: number;
  flatName: string;
  flatPrice: number;
  voicePrice: number;
  flatPriceRecurring: number;
  voicePriceRecurring: number;
  discountAmount: number;
  discountEndDate?: Date;
} = (isRecurring) => {
  const { dataContext } = useContext(DataContext);

  const entry = getEntry(dataContext);
  const option = entry && entry.options && entry.options[0];
  const selectedOption = dataContext.selectedOption;
  const hasVoiceOption = hasBasketEntryWithVoiceOption(dataContext);

  const flatName = selectedOption?.baseDisplayValue || "Flat Name";

  let flatPrice: number;
  let flatPriceRecurring: number;
  let voicePrice = 0;

  // TODO: replace 'flatPriceRecurring' value with option?.recurringCharges once it's fixed by backend
  // If the user picked a voice option, oneTimeCharge[0] is the voice price
  if (hasVoiceOption) {
    const voiceCharge = option?.oneTimeCharges?.[0];
    voicePrice = voiceCharge?.amount || voiceCharge?.unitAmount || 0;
    flatPrice = option?.oneTimeCharges?.[1]?.amount || 0;
    flatPriceRecurring = option?.oneTimeCharges?.[1]?.unitAmount || 0;
  }
  // Otherwise oneTimeCharge[0] is the flat price
  else {
    flatPrice = option?.oneTimeCharges?.[0]?.amount || 0;
    flatPriceRecurring = option?.oneTimeCharges?.[0]?.unitAmount || 0;
  }

  const voiceRecurringCharge = option?.recurringCharges?.find(
    (charge) => charge.chargeType?.id === ChargeTypeIDs.VoiceOption,
  );
  const voicePriceRecurring =
    voiceRecurringCharge?.unitAmount ??
    voiceRecurringCharge?.amount ??
    voicePrice;

  // Get basket totals with proper null checking
  const basketTotal = dataContext.basket?.total;
  const oneTimeAmount = basketTotal?.oneTime?.amount || 0;

  // For precharged products, we want to show the original price in line items
  // The discount will be shown separately, and the total will be 0
  const oneTimeCampaignAmount = basketTotal?.oneTimeCampaign?.amount || 0;
  const recurringAmount = basketTotal?.recurring?.amount || 0;
  const recurringUndiscountedAmount =
    basketTotal?.recurring?.undiscountedAmount ?? recurringAmount;

  // Calculate totals correctly
  const onceTotal = oneTimeAmount - oneTimeCampaignAmount;

  // Get the basket entry to determine SIM type
  const basketEntry = getEntry(dataContext);
  const isEsim = basketEntry?.esim ?? false;

  // Check if there's a Starterpaket discount
  const hasStarterpackageDiscount =
    dataContext.basket?.entries?.[0]?.options?.[0]?.discounts?.some(
      (discount) => discount.discountName === "Starterpaket",
    );

  // For precharged physical SIM cards with Starterpaket discount:
  // - Don't show subtotal and discount line items (we don't know if user paid for SIM)
  // For eSIMs with Starterpaket discount:
  // - Always show line items in the due now section
  // use oneTimeAmount for subtotal as 'undiscountedOneTimeAmount is not used for sim
  const onceSubtotal = oneTimeAmount;
  // Always use the basket's recurring amounts for accurate totals
  const recurringTotal = recurringAmount;
  const recurringSubtotal = recurringUndiscountedAmount;

  // Calculate discount amount based on context
  let discountAmount = 0;
  if (isRecurring) {
    // For recurring context, use the actual recurring discount amount from basket
    discountAmount = basketTotal?.recurring?.amountDiscounts || 0;
  } else {
    // For one-time context, discount is the campaign amount
    // But for precharged physical SIMs with Starterpaket, don't show discount line item
    if (hasStarterpackageDiscount && !isEsim) {
      discountAmount = 0; // Hide discount line item for precharged cards
    } else {
      // For eSIMs, always show the discount amount to ensure line items are displayed
      discountAmount = oneTimeCampaignAmount;
    }
  }

  const { campaignEndDate } = useGetCampaignEndDate(dataContext.basket);

  return {
    onceTotal,
    onceSubtotal,
    recurringTotal,
    recurringSubtotal,
    flatName,
    flatPrice,
    voicePrice,
    flatPriceRecurring,
    voicePriceRecurring,
    discountAmount,
    campaignEndDate,
  };
};

export const useGetIconForDevice = () => {
  const { dataContext } = useContext(DataContext);
  const entry = getEntry(dataContext);
  const device = entry && entry.device;

  const description =
    device?.description ||
    (isFlatHomeTariff(entry?.options?.[0].product?.id)
      ? "WLAN Home Router"
      : undefined);

  return getIconForDevice(description);
};

export const useHasVoiceOption = () => {
  const { dataContext } = useContext(DataContext);

  return hasBasketEntryWithVoiceOption(dataContext);
};

export const useBillingPeriod = () => {
  const { dataContext } = useContext(DataContext);

  return dataContext.selectedOption?.minContractDur || 30;
};

export const hasMobileAbo = (dataContext: DataContextInterface) => {
  const entry = getEntry(dataContext);
  const option = entry && entry.options && entry.options[0];

  return mobileAbos.includes(`${option?.product?.id}`);
};

export const useIsMobileAbo = () => {
  const { dataContext } = useContext(DataContext);

  return hasMobileAbo(dataContext);
};
